import BaseMap from 'components/BaseMap/BaseMap';
import MapCenterReporter from 'components/BaseMap/components/MapCenterReporter';
import IpawsAreaMapContent from 'features/Weather/features/WeatherAlerts/features/AlertCreator/features/AlertMapEditor/components/IpawsAreaMapContent/IpawsAreaMapContent';
import alertCreatorReducer from 'features/Weather/features/WeatherAlerts/features/AlertCreator/state/slices/alertCreatorSlice';
import { useMemo, useState } from 'react';
import { useGetFemaJurisdictionsQuery } from 'services/ipaws/ipaws.api';
import useLocation from 'hooks/useLocation';
import JurisdictionMapOverlay from 'features/Weather/features/WeatherAlerts/components/JurisdictionMapOverlay/JurisdictionMapOverlay';
import { getFemaJurisdictionBounds } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaJurisdictions.utils';
import { acceptFemaJurisdictionsForClass } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/utils/femaEnvironment.utils';

const IpawsDeviceMap = ({ draft, onAreaShapesChange, authorizedJurisdictions }) => {
  const { jurisdictionPolygons, locations } = useLocation();
  const mapCenter = draft.mapCenter
    ? [draft.mapCenter.lat, draft.mapCenter.lng]
    : locations.workplaceLocation.coords;
  const [currentMapCenter, setCurrentMapCenter] = useState({
    lat: mapCenter[0],
    lng: mapCenter[1],
  });
  // Multi-COG contract §1: one answer per class. The query asks for the action's class, and an
  // answer that echoes another class is not drawn. No class = no argument (an older backend).
  const femaEnvironment = draft.femaEnvironment ?? null;
  const femaJurisdictions = useGetFemaJurisdictionsQuery(
    femaEnvironment ? { environment: femaEnvironment } : undefined,
    { skip: Boolean(authorizedJurisdictions) },
  );
  const fetchedJurisdictions = acceptFemaJurisdictionsForClass(
    femaJurisdictions.data,
    femaEnvironment,
  ).data?.jurisdictions;
  const jurisdictionBoundaryRings = draft.resolvedJurisdiction?.boundaryRings;
  const jurisdictionBounds = useMemo(
    () => getFemaJurisdictionBounds({ boundaryRings: jurisdictionBoundaryRings }),
    [jurisdictionBoundaryRings],
  );

  // Run the same pure shape reducer as Alert Creator against this action's own draft.
  // The Global Action never writes to the Alert Creator's Redux draft.
  const dispatchAreaAction = (action) => {
    const nextState = alertCreatorReducer(
      { drafts: { ipaws: { areaShapes: draft.areaShapes ?? [] } } },
      action,
    );
    onAreaShapesChange(nextState.drafts.ipaws.areaShapes);
  };

  return (
    <div
      className="position-relative w-100 rounded-2 shadow overflow-hidden"
      style={{ height: 'clamp(360px, 58vh, 680px)' }}
    >
      <BaseMap
        mapCenter={mapCenter}
        zoom={13}
        bounds={jurisdictionBounds}
        className="position-absolute top-0 start-0 w-100 h-100"
        disableMapReset
      >
        <MapCenterReporter onCenterChange={setCurrentMapCenter} />
        <IpawsAreaMapContent
          alertData={draft}
          authorizedJurisdictions={authorizedJurisdictions ?? fetchedJurisdictions ?? []}
          currentMapCenter={currentMapCenter}
          severityColor="#f44336"
          dispatchAreaAction={dispatchAreaAction}
        />
        <JurisdictionMapOverlay
          workplacePolygons={jurisdictionPolygons}
          femaBoundaryRings={jurisdictionBoundaryRings ?? []}
        />
      </BaseMap>
    </div>
  );
};

export default IpawsDeviceMap;
